/**
 * The Career screens as widget lists.
 *
 * `career-menu.png` gives the four-button board exactly (the same widget as
 * the Main Menu's, five rows shorter); `career-enlist-name.png` gives the
 * enlistment dialog and the sentence plate under it. The two screens a sortie
 * comes home through moved to `debrief-screens.ts` when this file crossed the
 * size gate — they are the only ones `/fly` also reaches. The
 * AERODROME menu and VIEW PILOT RECORD have no capture — they are built from
 * the same measured parts and labelled as inference in `career-model.ts`.
 */

import {
  ENLISTMENT_DATES,
  ENLISTMENT_LABELS,
  enlistmentSentence,
  formatCareerDate,
  pilotRecord,
  SERVICE_LABELS,
  serviceFor,
  topAces,
  type CareerState,
  type TransferOption,
} from "@red-opus/engine";
import type { CareerSave } from "../../career/career-store.ts";
import { boardFrame, boardRows, boardSlotRect, type BoardSpec } from "../render/board-chrome.ts";
import type { Rect } from "../render/draw-ops.ts";
import { measureText, type FontAtlas } from "../render/text-metrics.ts";
import type { CareerModel } from "./career-model.ts";
import type { MenuAction } from "./menu-model.ts";
import { actionRow, boxValues, listScreen, segmentWidgets, wrapText } from "./panel-parts.ts";
import type { ListRow, ShellScreen, Widget } from "./shell-screen.ts";

const CAREER_BACKGROUND = "screens/MAINMENU.SCR";

/** `career-menu.png`: the Main Menu's board at four rows, its top edge at y=23. */
export const CAREER_MENU_BOARD: BoardSpec = {
  frame: { x: 6, y: 23, w: 148, h: 105 },
  rows: boardRows(4),
  columns: 1,
  columnWidth: 126,
  columnGap: 0,
  ridgeInsets: [21, 33],
  footerInsets: [27, 45],
};

const AERODROME_LAYOUT = { rows: boardRows(4), columns: 2, columnWidth: 130, columnGap: 9 } as const;

/** No capture exists; the wide board `fly-single-mission.png` measures is reused so the eight documented entries fit under a banner. */
export const AERODROME_BOARD: BoardSpec = {
  ...AERODROME_LAYOUT,
  frame: boardFrame(14, 40, AERODROME_LAYOUT),
  ridgeInsets: [39, 51],
  footerInsets: [54, 72],
};

interface BoardEntry {
  readonly id: string;
  readonly label: string;
  readonly action: MenuAction;
  readonly enabled?: boolean;
}

function boardScreen(
  id: string,
  title: string,
  spec: BoardSpec,
  entries: readonly BoardEntry[],
  background: string,
  cancel: MenuAction,
  caption: readonly string[] = [],
): ShellScreen {
  const buttons = entries.map((entry, index): Widget => ({
    kind: "button",
    id: entry.id,
    rect: boardSlotRect(spec, index),
    label: entry.label,
    enabled: entry.enabled !== false,
    action: entry.action,
  }));
  // The caption needs its own plate: on the aerodrome the board sits over a photograph, and engraved text on artwork is unreadable.
  const captionTop = spec.frame.y + spec.frame.h + 16;
  const lines: Widget[] = caption.length
    ? [
        { kind: "plate", rect: { x: spec.frame.x, y: captionTop, w: spec.frame.w, h: 6 + caption.length * 9 } },
        { kind: "prose", block: { lines: caption, x: spec.frame.x + 8, y: captionTop + 4, role: "label", pitch: 9 } },
      ]
    : [];
  return {
    id,
    chrome: {
      kind: "board",
      background,
      board: { frame: spec.frame, title, ridgeInsets: spec.ridgeInsets, footerInsets: spec.footerInsets },
    },
    widgets: [...buttons, ...lines],
    cancel,
  };
}

const command = (name: string): MenuAction => ({ kind: "command", command: name });

export function careerMenuScreen(hasPilots: boolean): ShellScreen {
  return boardScreen(
    "career/menu",
    "CAREER MENU",
    CAREER_MENU_BOARD,
    [
      { id: "start", label: "START A CAREER", action: command("start-career") },
      { id: "continue", label: "CONTINUE A CAREER", action: command("roster"), enabled: hasPilots },
      { id: "aces", label: "VIEW TOP ACES", action: command("top-aces"), enabled: hasPilots },
      { id: "return", label: "RETURN TO MAIN", action: { kind: "navigate", path: "/" } },
    ],
    CAREER_BACKGROUND,
    { kind: "navigate", path: "/" },
  );
}

/** The AERODROME menu, verbatim from `ui-menus.md`'s eight-entry list. */
export function aerodromeScreen(career: CareerState, background: string, caption: readonly string[]): ShellScreen {
  const grounded = career.status !== "active";
  const recovering = career.status === "hospital" || career.status === "pow";
  return boardScreen(
    "career/aerodrome",
    "AERODROME",
    AERODROME_BOARD,
    [
      recovering
        ? { id: "resume", label: "REPORT BACK FOR DUTY", action: command("resume") }
        : { id: "fly", label: "FLY NEXT MISSION", action: command("assignment"), enabled: !grounded },
      { id: "squadron", label: "SQUADRON INFO", action: command("notice:squadron"), enabled: false },
      { id: "map", label: "MAP OF THE FRONT", action: command("notice:map"), enabled: false },
      { id: "transfer", label: "REQUEST TRANSFER", action: command("transfer"), enabled: !grounded },
      { id: "aircraft", label: "PERSONAL AIRCRAFT", action: command("aircraft"), enabled: !grounded },
      { id: "record", label: "VIEW PILOT RECORD", action: command("record") },
      // Grounded like its neighbours: a pilot in hospital or a prison camp has
      // no career progress to duplicate that he did not already have, and a
      // dead one's board is greyed entire. The *restore* half needs no entry
      // here — the manual routes it through CONTINUE A CAREER.
      { id: "backup", label: "BACKUP CAREER", action: command("backup"), enabled: !grounded },
      { id: "return", label: "RETURN TO MAIN", action: { kind: "navigate", path: "/" } },
    ],
    background,
    command("menu"),
    caption,
  );
}

/** The roster's own rows: the pilot, his rank, and his score in the trailing column. */
export function rosterRows(saves: readonly CareerSave[]): ListRow[] {
  return saves.map((save) => {
    const record = pilotRecord(save.career);
    return { id: save.career.id, label: `${record.name}  ${record.rank}`, trailing: `${record.totalVictories}` };
  });
}

/** VIEW TOP ACES, which ranks the same saves by victories rather than listing them. */
export function aceRows(saves: readonly CareerSave[]): ListRow[] {
  return topAces(saves.map((save) => save.career)).map((entry, index) => ({
    id: `${index}`,
    label: `${entry.name}  ${entry.rank}`,
    trailing: `${entry.victories}`,
  }));
}

/** CONTINUE A CAREER — the saved pilots in the original's list-box style. */
export function careerRosterScreen(model: CareerModel, rows: readonly ListRow[], font: FontAtlas): ShellScreen {
  return listScreen(
    {
      id: "career/roster",
      title: "CONTINUE A CAREER",
      prompt: "SELECT A PILOT:",
      rows,
      actions: [
        { id: "cancel", label: "CANCEL", action: command("menu") },
        { id: "delete", label: "DELETE", action: command("delete"), enabled: rows.length > 0 },
        { id: "select", label: "SELECT", action: command("select"), enabled: rows.length > 0 },
      ],
    },
    model,
    font,
  );
}

export function topAcesScreen(model: CareerModel, rows: readonly ListRow[], font: FontAtlas): ShellScreen {
  return listScreen(
    {
      id: "career/top-aces",
      title: "VIEW TOP ACES",
      prompt: "TOP ACES:",
      rows,
      actions: [{ id: "cancel", label: "CANCEL", action: command("menu") }],
    },
    model,
    font,
  );
}

/** `career-enlist-name.png`: the name dialog above, the live enlistment sentence on a full-width plate below. */
export const ENLIST_DIALOG: Rect = { x: 83, y: 54, w: 169, h: 53 };
export const ENLIST_FIELD: Rect = { x: 93, y: 68, w: 109, h: 13 };
export const ENLIST_PLATE: Rect = { x: 0, y: 128, w: 320, h: 72 };

export function enlistScreen(model: CareerModel, font: FontAtlas, heading: FontAtlas): ShellScreen {
  const service = SERVICE_LABELS[serviceFor(model.nation, ENLISTMENT_DATES[model.period])];
  const sentence = enlistmentSentence(model.name, model.nation, 0, ENLISTMENT_DATES[model.period]);
  const values = [phraseOf(model), model.name, service].filter((value) => value !== "");
  const title = `Enlistment in the ${service}`;
  return {
    id: "career/enlist",
    chrome: { kind: "panel", title: "START A CAREER", emblem: model.nation === "german" ? "cross" : "roundel", background: CAREER_BACKGROUND },
    widgets: [
      { kind: "plate", rect: ENLIST_DIALOG },
      { kind: "text", text: "WHAT IS YOUR PILOT'S NAME?", x: 89, y: 59, role: "prompt" },
      { kind: "well", rect: ENLIST_FIELD },
      { kind: "text", text: `${model.name}_`, x: 97, y: 71, role: "label" },
      { kind: "plate", rect: ENLIST_PLATE },
      { kind: "text", text: title, x: 34, y: 134, role: "heading" },
      ...segmentWidgets(boxValues(sentence, values), { x: 24, y: 152, maxWidth: 272, pitch: 10, maxLines: 2 }, font),
      ...actionRow(
        [
          { id: "cancel", label: "CANCEL", action: command("menu") },
          { id: "period", label: "WHEN?", action: command("period") },
          { id: "start", label: "START", action: command("enlist"), enabled: model.name.trim() !== "" },
        ],
        heading,
        176,
      ),
    ],
    cancel: command("menu"),
  };
}

/** The war-phase clause the original boxes in the enlistment sentence, from the engine's own label table. */
export function phraseOf(model: CareerModel): string {
  return ENLISTMENT_LABELS[model.period];
}


/**
 * REQUEST TRANSFER — `Pick a Squadron:` over the postings this pilot could ask
 * for, with the squadron's own answer above the list.
 *
 * No capture exists. The strings are `PS.EXE`'s, and its own string block gives
 * the shape: `"You must attain the rank of %b before the %s will accept a
 * transfer request."`, `"Due to your recent transfer, …"`, `Pick a Squadron:`,
 * `DECLINE`, `ACCEPT`, `"You have been transferred to %q stationed at %p%l."`
 * run consecutively — a squadron list with a two-button answer and a refusal
 * over it. `campaign.md` § *Squadron transfers* reads the block the same way.
 *
 * **Every** postable squadron is listed, not only the eligible ones, because
 * the refusals are the interesting half: a pilot who cannot yet get into J.G.1
 * should be told he needs the rank, in the game's words, rather than find the
 * unit missing from a list and conclude it does not exist. `career-actions.ts`
 * fills the slots; nothing here composes a refusal of its own.
 *
 * `DECLINE` is the original's word for backing out of the offer, so it stands
 * where a CANCEL would — the same button, the game's label.
 */
export function transferScreen(
  cursor: { readonly listIndex: number; readonly listTop: number },
  options: readonly TransferOption[],
  font: FontAtlas,
  heading: FontAtlas,
): ShellScreen {
  const rows: ListRow[] = options.map((option) => ({
    id: option.squadron.id,
    label: option.squadron.name,
    trailing: option.squadron.aerodrome,
  }));
  const chosen = options[cursor.listIndex];
  const message = transferMessage(chosen);
  return {
    id: "career/transfer",
    chrome: { kind: "panel", title: "REQUEST TRANSFER", emblem: "none", background: undefined },
    widgets: [
      { kind: "plate", rect: { x: 16, y: 24, w: 286, h: 118 } },
      { kind: "text", text: "Pick a Squadron:", x: 27, y: 28, role: "prompt" },
      ...(rows.length > 0
        ? [
            {
              kind: "list" as const,
              id: "rows",
              rect: { x: 28, y: 40, w: 262, h: 60 },
              rows,
              selected: cursor.listIndex,
              top: cursor.listTop,
              action: command("accept-transfer"),
            },
          ]
        : []),
      { kind: "prose", block: { lines: wrapText(font, message, 268), x: 27, y: 104, role: "label", pitch: 9 } },
      ...actionRow(
        [
          { id: "decline", label: "DECLINE", action: command("aerodrome") },
          { id: "accept", label: "ACCEPT", action: command("accept-transfer"), enabled: chosen?.eligible === true },
        ],
        heading,
        158,
      ),
    ],
    cancel: command("aerodrome"),
  };
}

/**
 * What prints under the list. A refusal is the squadron's, verbatim from
 * `career-actions.ts`; the acceptance line is this port's own prompt, because
 * `PS.EXE`'s success string (`"You have been transferred to %q stationed at
 * %p%l."`) is past tense and belongs after the button, not before it — the
 * career log prints exactly that when the transfer goes through.
 */
export function transferMessage(chosen: TransferOption | undefined): string {
  if (!chosen) return "No other squadron on this front will take a posting from yours.";
  if (!chosen.eligible) return chosen.refusal;
  return `${chosen.squadron.name} will have you. They are stationed at ${chosen.squadron.aerodrome}.`;
}

/** VIEW PILOT RECORD, in the original's own field order (`ui-menus.md`); no capture exists, so the layout is this port's. */
export function pilotRecordScreen(
  career: CareerState,
  rows: readonly { label: string; value: string }[],
  font: FontAtlas,
  heading: FontAtlas,
): ShellScreen {
  const half = Math.ceil(rows.length / 2);
  const cell = (row: { label: string; value: string }, index: number): Widget[] => {
    const x = index < half ? 24 : 168;
    const y = 36 + (index % half) * 9;
    return [
      { kind: "text", text: row.label, x, y, role: "label" },
      { kind: "text", text: row.value, x: x + 124 - measureText(font, row.value), y, role: "label" },
    ];
  };
  return {
    id: "career/record",
    chrome: { kind: "panel", title: "VIEW PILOT RECORD", emblem: career.nation === "german" ? "cross" : "roundel", background: undefined },
    widgets: [
      { kind: "plate", rect: { x: 14, y: 26, w: 292, h: 140 } },
      { kind: "text", text: formatCareerDate(career.date), x: 24, y: 156, role: "label" },
      ...rows.flatMap(cell),
      ...actionRow([{ id: "back", label: "RETURN TO AERODROME", action: command("aerodrome") }], heading, 178),
    ],
    cancel: command("aerodrome"),
  };
}
